import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import i18next from 'i18next';
import AuthWrapperContainer from '~/containers/AuthWrapper';
import ConfirmationContainer from '~/containers/Confirmation';
import { ShowOnRootProvider } from './context/ShowOnRoot';
import initServices from './services';

// Import pages
import RootPage from './pages/RootPage';
import LoginPage from './pages/login/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import AlertsPage from './pages/alerts/AlertsListPage';
import AlertDetailPage from './pages/alerts/AlertDetailPage';
import ResidentsPage from './pages/residents/ResidentsListPage';
import ResidentDetailPage from './pages/residents/ResidentDetailPage';
import ResidentEditPage from './pages/residents/ResidentEditPage';
import ResidentCreatePage from './pages/residents/ResidentCreatePage';
import HealthcareProvidersPage from './pages/healthcare-providers/HealthcareProvidersListPage';
import HealthcareProviderEditPage from './pages/healthcare-providers/HealthcareProviderEditPage';
import HealthcareProviderCreatePage from './pages/healthcare-providers/HealthcareProviderCreatePage';
import ConfigurationPage from './pages/configuration/ConfigurationPage';
import HcpGroupsPage from './pages/hcp-groups/HcpGroupsPage';
import SiteListPage from './pages/SiteList';
import SiteCreatePage from './pages/SiteCreate';
import SitePlaneListPage from './pages/SitePlaneList';
import SitePlaneCreatePage from './pages/SitePlaneCreate';
import SitePlaneMapPage from './pages/SitePlaneMap';
import SitePlaneEditPage from './pages/SitePlaneEdit';
import DeviceConfigPage from './pages/DeviceConfig';
import DeviceBulkConfigPage from './pages/DeviceBulkConfig';
import DeviceSiteConfigPage from './pages/DeviceSiteConfig';
import DeviceHealthPage from './pages/DeviceHealth';
import DeviceOverviewPage from './pages/DeviceOverview';
import StatisticsPage from './pages/statistics/StatisticsPage';
import FirmwareListPage from './pages/FirmwareListPage';
import ErrorPage from './pages/ErrorPage';

const App: React.FC = () => {
  // Force a full re-render whenever the i18n language changes so every `t(...)` call resolves with the new locale.
  const [, setLanguageTick] = React.useState(i18next.language);
  React.useEffect(() => {
    const handler = (lng: string) => setLanguageTick(lng);
    i18next.on('languageChanged', handler);
    return () => i18next.off('languageChanged', handler);
  }, []);

  React.useEffect(() => {
    initServices();
  }, []);

  return (
    <ShowOnRootProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<AuthWrapperContainer isAuthScreen={false} />}>
          <Route index element={<RootPage />} />
        </Route>
        <Route path="/login" element={<AuthWrapperContainer isAuthScreen={false} />}>
          <Route index element={<LoginPage />} />
        </Route>
        <Route path="/forgot-password" element={<AuthWrapperContainer isAuthScreen={false} />}>
          <Route index element={<ForgotPasswordPage />} />
        </Route>
        <Route path="/reset-password" element={<AuthWrapperContainer isAuthScreen={false} />}>
          <Route index element={<ResetPasswordPage />} />
        </Route>
        <Route path="/accept-invite" element={<AuthWrapperContainer isAuthScreen={false} />}>
          <Route index element={<AcceptInvitePage />} />
        </Route>

        {/* Protected routes */}
        <Route path="/*" element={<AuthWrapperContainer isAuthScreen={true} />}>
          <Route path="dashboard" element={<DashboardPage />} />

          <Route path="alerts" element={<AlertsPage />} />
          <Route path="alerts/:id" element={<AlertDetailPage />} />

          <Route path="residents" element={<ResidentsPage />} />
          <Route path="residents/create" element={<ResidentCreatePage />} />
          <Route path="residents/:id" element={<ResidentDetailPage />} />
          <Route path="residents/:id/edit" element={<ResidentEditPage />} />

          <Route path="healthcare-providers" element={<HealthcareProvidersPage />} />
          <Route path="healthcare-providers/create" element={<HealthcareProviderCreatePage />} />
          <Route path="healthcare-providers/:id/edit" element={<HealthcareProviderEditPage />} />

          <Route path="configuration" element={<ConfigurationPage />} />

          <Route path="hcp-groups" element={<HcpGroupsPage />} />

          <Route path="sites" element={<SiteListPage />} />
          <Route path="sites/create" element={<SiteCreatePage />} />
          <Route path="sites/:id" element={<SitePlaneListPage />} />
          <Route path="sites/:id/create" element={<SitePlaneCreatePage />} />
          <Route path="sites/:id/:planeId" element={<SitePlaneMapPage />} />
          <Route path="sites/:id/:planeId/edit" element={<SitePlaneEditPage />} />

          <Route path="device-health" element={<DeviceHealthPage />} />
          <Route path="device-overview" element={<DeviceOverviewPage />} />
          <Route path="device-overview/configuration" element={<DeviceBulkConfigPage />} />
          <Route path="device-overview/site-configuration" element={<DeviceSiteConfigPage />} />
          <Route path="device-overview/:uuid/configuration" element={<DeviceConfigPage />} />

          <Route path="statistics" element={<StatisticsPage />} />
          <Route path="firmware" element={<FirmwareListPage />} />
          {/* The page used to live here; keep old links and bookmarks working. */}
          <Route path="billing" element={<Navigate to="/statistics" replace />} />

          {/* Error routes */}
          <Route path="404" element={<ErrorPage statusCode={404} />} />
          <Route path="500" element={<ErrorPage statusCode={500} />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
      </Routes>
      <ConfirmationContainer />
    </ShowOnRootProvider>
  );
};

export default observer(App);
